import * as React from 'react';

import { translate } from '~l10n/client';

import { CreateQuizEventAdapter } from '~analytics/adapters/CreateQuizEventAdapter';
import { PaywallScreens } from '~analytics/types';
import { Spinner } from '~shared/components/Spinner/Spinner';
import { toast } from '~shared/components/Toast';
import { Direction } from '~shared/utils/sort';
import {
  PaywallDialogGeneric,
  PaywallDispatch,
  usePaywallReducer,
} from '~teacher/components/PaywallDialog';
import {
  DispatchColumnSortAction,
  QuizList,
  QuizListColumn,
} from '~teacher/components/quizzes';
import {
  CopyToWorkspaceDialog,
  useCopyQuizToWorkspaceDialog,
} from '~teacher/components/quizzes/CopyMoveDialog';
import { MergeQuizDialog } from '~teacher/components/quizzes/MergeQuizDialog/MergeQuizDialog';
import {
  DispatchMergeQuizDialogAction,
  OnMergeQuiz,
  QuizToMerge,
  useMergeQuizDialog,
} from '~teacher/components/quizzes/MergeQuizDialog/useMergeQuizDialog';
import {
  QuizLibraryActions,
  useDeleteSelectedQuizFolder,
} from '~teacher/components/quizzes/QuizLibraryActions';
import { QuizLimit } from '~teacher/components/quizzes/QuizLimit';
import {
  LibraryFolderActionProvider,
  LibraryQuizActionProvider,
} from '~teacher/components/quizzes/QuizListItem/ActionProviders';
import { RenameFolderDialog } from '~teacher/components/quizzes/RenameFolderDialog';
import {
  QuizToastActions,
  apiToaster,
} from '~teacher/components/quizzes/toastActions';
import { VirtualQuizFolderList } from '~teacher/components/quizzes/VirtualQuizFolderList';
import {
  DeleteQuizConfirmation,
  QuizzesPaywall,
  useDeleteQuizConfirmation,
} from '~teacher/components/QuizzesPaywall';
import { ShareQuizDialog } from '~teacher/components/ShareQuizDialog/ShareQuizDialog';
import { useShareDialog } from '~teacher/components/ShareQuizDialog/useShareDialog';
import {
  UseUpdateQuizOptions,
  useQuizLimit,
  useQuizzes,
  useUpdateQuiz,
} from '~teacher/hook';
import { useDebouncedCallback } from '~teacher/hook/useDebouncedCallback';
import { useMergeQuiz } from '~teacher/hook/useMergeQuiz';
import { useRenameFolderDialog } from '~teacher/pages/useRenameFolderDialog';
import { proCheck } from '~teacher/util/proCheck';

import { uniqueIdFactory } from '../../../../api/quiz/getQuizzes/normalize/uniqueIds';
import { Quiz, QuizOrFolder } from '../../../../api/quiz/getQuizzes/types';
import { QuizUrl, UpdateQuizParameters } from '../../../../api/quizApi';
import {
  MoveQuizFolderDialog,
  useMoveFolderDialog,
} from '../MoveQuizFolderDialog';

interface Props {
  parentId?: number;
  sortDirection: Direction;
  sortProperty: QuizListColumn;
  dispatchColumnSortAction: DispatchColumnSortAction;
  quizItems: QuizOrFolder[];
  isSelectedAll: boolean | 'mixed';
  selectedQuizItem: Set<string>;
  toggleSelectAll: () => void;
  toggleItem: (uniqueId: string) => void;
  selectNone: () => void;
  setParentId: (newParentId: number) => void;
  closeSearchField?: () => void;
  QuizUrl: QuizUrl;
}

interface UseHandleMergeQuizParams {
  featurePaywallDispatch: PaywallDispatch;
  quizLimitPaywallDispatch: PaywallDispatch;
  selectedQuizzes: Quiz[];
  selectNone: () => void;
  canMergeQuizzes: boolean;
}

interface OpenMergeQuizDialogParams {
  quizzesToMerge: QuizToMerge[];
  onMergeQuiz: OnMergeQuiz;
}

const useOpenMergeQuizDialog = ({
  quizLimitPaywallDispatch,
  canMergeQuizzes,
  featurePaywallDispatch,
  dispatch,
}: {
  quizLimitPaywallDispatch: PaywallDispatch;
  canMergeQuizzes: boolean;
  featurePaywallDispatch: PaywallDispatch;
  dispatch: DispatchMergeQuizDialogAction;
}) => {
  const quizLimit = useQuizLimit();

  if (!canMergeQuizzes)
    return () => {
      featurePaywallDispatch({
        type: 'OPEN',
        touch: [PaywallScreens.QuizListMergeQuiz],
      });
    };

  if (quizLimit.isLoading) return () => {};

  if (quizLimit.data.currentQuizCount >= quizLimit.data.limit) {
    return () =>
      quizLimitPaywallDispatch({
        type: 'OPEN',
        touch: [
          PaywallScreens.QuizListLimitReached,
          PaywallScreens.QuizListMergeQuiz,
        ],
      });
  }

  return ({ quizzesToMerge, onMergeQuiz }: OpenMergeQuizDialogParams) =>
    dispatch({ type: 'OPEN', payload: { quizzesToMerge, onMergeQuiz } });
};

function useHandleMergeQuiz({
  featurePaywallDispatch,
  quizLimitPaywallDispatch,
  selectedQuizzes,
  selectNone,
  canMergeQuizzes,
}: UseHandleMergeQuizParams) {
  const [state, dispatch] = useMergeQuizDialog();

  const closeMergeQuizDialog = React.useCallback(() => {
    dispatch({ type: 'CLOSE' });
  }, [dispatch]);

  const openMergeQuizDialog = useOpenMergeQuizDialog({
    canMergeQuizzes: canMergeQuizzes,
    dispatch: dispatch,
    featurePaywallDispatch: featurePaywallDispatch,
    quizLimitPaywallDispatch: quizLimitPaywallDispatch,
  });

  const mergeQuizMutation = useMergeQuiz();
  const handleMergeQuiz = () => {
    openMergeQuizDialog({
      quizzesToMerge: selectedQuizzes.map(({ name, soc }) => ({
        quizId: soc,
        quizName: name,
      })),
      onMergeQuiz: ({
        quizName,
        quizOrder,
      }: {
        quizName: string;
        quizOrder: string[];
      }) => {
        const [quizSoc1, quizSoc2] = quizOrder;
        return mergeQuizMutation.mutateAsync(
          {
            name: quizName,
            quizSoc1,
            quizSoc2,
            parentId: selectedQuizzes[0]?.parentId,
          },
          {
            onSuccess: () => {
              new CreateQuizEventAdapter({
                creation_method: 'merge',
                number_of_quizzes: 2,
              }).sendAnalytics();
              apiToaster([quizName], QuizToastActions.MERGE);
              closeMergeQuizDialog();
              selectNone();
            },
            onError: (error: unknown) => {
              if (error instanceof Error) {
                apiToaster([], QuizToastActions.MERGE, error);
              }
            },
          }
        );
      },
    });
  };

  return {
    isMergeQuizDialogOpen: state.isOpen,
    quizzesToMerge: state.quizzesToMerge,
    onMergeQuiz: state.onMergeQuiz,
    closeMergeQuizDialog,
    handleMergeQuiz,
  };
}

export function LibraryPanel({
  toggleSelectAll,
  isSelectedAll,
  sortDirection,
  sortProperty,
  dispatchColumnSortAction,
  quizItems,
  selectedQuizItem,
  toggleItem,
  selectNone,
  setParentId,
  closeSearchField,
  QuizUrl,
}: Props) {
  const quizzes = useQuizzes();
  const selectedQuizItems = useQuizzes<Quiz[]>({
    select: (data) =>
      [...selectedQuizItem]
        .map((quizId) => data?.itemById[quizId] ?? null)
        .filter((quiz) => quiz?.type === 'quiz') as Quiz[],
  });
  const updateQuizAction = useUpdateQuiz();

  const { deleteSelectedQuizFolder } = useDeleteSelectedQuizFolder();

  const [{ open: quizLimitPaywall, touch }, quizLimitPaywallDispatch] =
    usePaywallReducer();
  const [{ open: featurePaywallOpen }, featurePaywallDispatch] =
    usePaywallReducer();

  const {
    isShareDialogOpen,
    isSharingEnabled,
    soc,
    dispatch: shareDialogDispatch,
    quizId,
    name,
  } = useShareDialog();

  const { renameFolderId, onCloseRenameFolder, onOpenRenameFolder } =
    useRenameFolderDialog();

  const initialRenameFolderName = React.useMemo(() => {
    if (!renameFolderId) return null;
    const uniqueId = uniqueIdFactory['folder'].toUniqueId(renameFolderId);
    const folder = quizzes.data?.itemById[uniqueId] ?? null;
    if (!folder) return null;
    return folder.name;
  }, [renameFolderId, quizzes.data]);

  const {
    isMergeQuizDialogOpen,
    quizzesToMerge,
    onMergeQuiz,
    closeMergeQuizDialog,
    handleMergeQuiz,
  } = useHandleMergeQuiz({
    featurePaywallDispatch: featurePaywallDispatch,
    quizLimitPaywallDispatch: quizLimitPaywallDispatch,
    selectedQuizzes: selectedQuizItems?.data ?? [],
    selectNone: selectNone,
    canMergeQuizzes: proCheck.canMergeQuizzes(),
  });

  const [deleteQuizConfirmationState, dispatchDeleteQuizConfirmation] =
    useDeleteQuizConfirmation();

  const onCloseQuizzesPaywall = React.useCallback(
    () => quizLimitPaywallDispatch({ type: 'CLOSE' }),
    [quizLimitPaywallDispatch]
  );

  const onClosePaywallGeneric = React.useCallback(
    () => featurePaywallDispatch({ type: 'CLOSE' }),
    [featurePaywallDispatch]
  );

  const debouncedUpdateQuiz = useDebouncedCallback({
    callback: (
      updateQuizParameters: UpdateQuizParameters,
      useUpdateQuizOptions: UseUpdateQuizOptions
    ) => updateQuizAction.mutate(updateQuizParameters, useUpdateQuizOptions),
    dependencies: [quizId, name],
    interval: 1000,
  });

  const handleEnableSharing = () => {
    shareDialogDispatch({ type: 'ToggleSharing' });
    debouncedUpdateQuiz(
      {
        quizUrl: QuizUrl,
        quizId: quizId,
        name: name,

        isSharable: !isSharingEnabled,
      },
      {
        onError: () => {
          shareDialogDispatch({ type: 'ToggleSharing' });
          toast.critical(translate('An error has occurred'));
        },
      }
    );
  };

  const {
    openMoveDialog,
    closeMoveDialog,
    moveDialogState: { itemsIdsToMove, isOpen: isMoveFolderDialogOpen },
  } = useMoveFolderDialog(selectNone, setParentId, featurePaywallDispatch);

  const {
    close: closeCopyDialog,
    open: openCopyDialog,
    state: { isOpen: isCopyDialogOpen, selectedItemIds: selectedCopyItems },
  } = useCopyQuizToWorkspaceDialog(selectNone, setParentId);

  const openMoveQuizDialog = () => openMoveDialog(...selectedQuizItem);
  const openCopyQuizToWorkspaceDialog = () =>
    openCopyDialog(...selectedQuizItem);

  const deleteFolderAction = React.useCallback(
    (folderId: number) =>
      deleteSelectedQuizFolder({
        selectedQuizItem: [uniqueIdFactory.folder.toUniqueId(folderId)],
        onError: () => {
          toast.critical(translate('An error occurred while trying to delete'));
        },
      }),
    [deleteSelectedQuizFolder]
  );

  const onDeleteQuiz = React.useCallback(
    (quizId: number) => {
      if (proCheck.canRestoreTrash()) {
        deleteSelectedQuizFolder({
          selectedQuizItem: [uniqueIdFactory.quiz.toUniqueId(quizId)],
        });
      } else {
        dispatchDeleteQuizConfirmation({
          type: 'open',
          payload: {
            deleteAction: () =>
              deleteSelectedQuizFolder({
                selectedQuizItem: [uniqueIdFactory.quiz.toUniqueId(quizId)],
                onSuccess: () =>
                  dispatchDeleteQuizConfirmation({
                    type: 'close',
                  }),
                onError: () => {
                  toast.critical(translate('An error has occurred'));
                },
              }),
          },
        });
      }
    },
    [deleteSelectedQuizFolder, dispatchDeleteQuizConfirmation]
  );

  const deleteSelectedQuizFolderHandler = React.useCallback(() => {
    // check for pro delete right away
    if (proCheck.canRestoreTrash()) {
      deleteSelectedQuizFolder({
        selectedQuizItem: [...selectedQuizItem],
        onError: () => {
          toast.critical(translate('An error has occurred'));
        },
      });
    } else {
      dispatchDeleteQuizConfirmation({
        type: 'open',
        payload: {
          deleteAction: () => {
            deleteSelectedQuizFolder({
              selectedQuizItem: [...selectedQuizItem],
              onError: () => {
                toast.critical(
                  translate('An error occurred while trying to delete')
                );
              },
              onSuccess: () =>
                dispatchDeleteQuizConfirmation({ type: 'close' }),
            });
          },
        },
      });
    }
  }, [
    deleteSelectedQuizFolder,
    selectedQuizItem,
    dispatchDeleteQuizConfirmation,
  ]);

  return (
    <>
      <LibraryFolderActionProvider
        onOpenRenameFolder={onOpenRenameFolder}
        onDeleteFolder={deleteFolderAction}
        openMoveDialog={openMoveDialog}
      >
        <LibraryQuizActionProvider
          onDeleteQuiz={onDeleteQuiz}
          openMoveDialog={openMoveDialog}
          openCopyDialog={(selectedItem) => openCopyDialog(selectedItem)}
        >
          <QuizLimit />
          <QuizList
            selectedAll={isSelectedAll}
            onChangeSelect={toggleSelectAll}
            sortDirection={sortDirection}
            sortProperty={sortProperty}
            dispatchColumnSortAction={dispatchColumnSortAction}
            selectedQuizItem={selectedQuizItem}
            actions={
              <QuizLibraryActions
                openMoveQuizDialog={openMoveQuizDialog}
                openCopyQuizDialog={openCopyQuizToWorkspaceDialog}
                openMergeQuizDialog={handleMergeQuiz}
                selectedQuizItem={selectedQuizItem}
                deleteSelectedQuizFolder={deleteSelectedQuizFolderHandler}
              />
            }
          >
            {quizzes.status === 'loading' && (
              <div className="pt-15">
                <Spinner size="small" />
              </div>
            )}
            <VirtualQuizFolderList
              shareDialogDispatch={shareDialogDispatch}
              selectedQuizItem={selectedQuizItem}
              toggleItem={toggleItem}
              items={quizItems}
              closeSearchField={closeSearchField}
              paywallDispatch={quizLimitPaywallDispatch}
            />
          </QuizList>
        </LibraryQuizActionProvider>
      </LibraryFolderActionProvider>
      <QuizzesPaywall
        open={quizLimitPaywall}
        onClose={onCloseQuizzesPaywall}
        paywallTouch={touch}
      />
      <PaywallDialogGeneric
        open={featurePaywallOpen}
        onClose={onClosePaywallGeneric}
        paywallScreen={PaywallScreens.QuizListAddFolder}
      />
      <MergeQuizDialog
        isOpen={isMergeQuizDialogOpen}
        quizzesToMerge={quizzesToMerge}
        onClose={closeMergeQuizDialog}
        onMergeQuiz={onMergeQuiz}
      >
        <span className="text-base text-blue-900">
          {translate('Note: Standards will not transfer to the merged quiz.')}
        </span>
      </MergeQuizDialog>
      <RenameFolderDialog
        renameFolderId={renameFolderId}
        initialRenameFolderName={initialRenameFolderName}
        onCloseRenameFolder={onCloseRenameFolder}
      />
      <ShareQuizDialog
        isOpen={isShareDialogOpen}
        title={`${translate('Share Quiz')}`}
        isSharingEnabled={isSharingEnabled}
        socNumber={soc}
        handleEnableSharing={handleEnableSharing}
        isControlsDisabled={false}
        closeDialog={() => {
          shareDialogDispatch({
            type: 'Close',
          });
        }}
      />
      <DeleteQuizConfirmation
        onDelete={deleteQuizConfirmationState.deleteAction}
        open={deleteQuizConfirmationState.isOpen}
        onClose={() => dispatchDeleteQuizConfirmation({ type: 'close' })}
      />
      <MoveQuizFolderDialog
        itemIdsToMove={itemsIdsToMove}
        onClose={closeMoveDialog}
        isOpen={isMoveFolderDialogOpen}
      />
      <CopyToWorkspaceDialog
        itemIdsToMove={selectedCopyItems}
        onClose={closeCopyDialog}
        isOpen={isCopyDialogOpen}
      />
    </>
  );
}
